import Menu from "./ResponsiveMenu";
import { cardPillSizingSx } from "./buttonSizing";
import React, { useId, useState } from "react";
import { Box, Button, MenuItem, Typography, useMediaQuery } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import type { SxProps, Theme } from "@mui/material/styles";
import { ExpandMore, Check } from "@mui/icons-material";
import { storyLibraryPillButtonSx } from "./StoryLibraryControls";
import { OptionSelectionIndicator, storyActionSheetPaperSx, storyActionSheetItemSx, storyActionSheetIconSx } from './StoryFormControls';

export default function PillMenu<T extends string | number>({ label, value, options, onChange, compact = false, sx, mobileSheet = true, plainIcons = false }: {
  label: string; value: T; options: { value: T; label: string; icon?: React.ReactNode }[]; onChange: (value: T) => void;
  compact?: boolean;
  mobileSheet?: boolean;
  plainIcons?: boolean;
  sx?: SxProps<Theme>;
}) {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const id = useId();
  const mobile = useMediaQuery(useTheme().breakpoints.down('md'));
  const sheet = mobile && mobileSheet;
  const selected = options.find(option => option.value === value);
  return <>
    <Button aria-label={`${label}: ${selected?.label}`} aria-haspopup="menu" startIcon={selected?.icon}
      aria-expanded={!!anchor} aria-controls={anchor ? id : undefined} endIcon={<ExpandMore />}
      sx={[{ ...storyLibraryPillButtonSx(false), ...(compact ? cardPillSizingSx : {}) }, ...(Array.isArray(sx) ? sx : [sx])]}
      onClick={event => setAnchor(event.currentTarget)}>
      {options.find(option => option.value === value)?.label}
    </Button>
    <Menu sheetTitle={label} id={id} anchorEl={anchor} open={!!anchor} onClose={() => setAnchor(null)} MenuListProps={{ "aria-label": label }}
      slotProps={{ paper: { sx: mobileSheet ? {
        ...storyActionSheetPaperSx,
      } : { bgcolor: "#1B1E28", color: "white", border: "1px solid #3b3d45", borderRadius: "16px", minWidth: 160 } } }}>
      {mobileSheet && !sheet && <Typography component="li" sx={{ listStyle: 'none', px: 2.5, py: 1.5, fontWeight: 700, fontSize: 20 }}>{label}</Typography>}
      {options.map(option => <MenuItem key={option.value} selected={option.value === value} role="menuitemradio" aria-checked={option.value === value}
        onClick={() => { onChange(option.value); setAnchor(null); }} sx={mobileSheet ? storyActionSheetItemSx : { gap: 2, minHeight: 44, justifyContent: "space-between" }}>
        {option.icon && <Box component="span" sx={{ ...storyActionSheetIconSx,
          ...(plainIcons ? { width: 22, minWidth: 22, height: 22, bgcolor: 'transparent' } : {}),
        }}>{option.icon}</Box>}
        <Box component="span" sx={{ flex: 1 }}>{option.label}</Box>
        {mobileSheet ? <OptionSelectionIndicator selected={option.value === value} /> : option.value === value && <Check fontSize="small" sx={{ color: "#D8A363" }} />}
      </MenuItem>)}
    </Menu>
  </>;
}
